import { Button } from '@/components/ui/shadcn/button'
import { Input } from '@/components/ui/shadcn/input'
import { useCompanySetupStore } from '@/stores/companySetup/companySetupStore'
import { ArrowLeft, ArrowRight } from 'lucide-react'
import { useState } from 'react'

interface Props {
  onNext: (url: string) => void
  onSkip: () => void
  onBack?: () => void
  // Prefill when editing an existing board's website (wins over the stored value).
  initialUrl?: string
}

export function CompanySetupUrlStep({ onNext, onSkip, onBack, initialUrl }: Props) {
  const { url, setUrl } = useCompanySetupStore()
  const [localUrl, setLocalUrl] = useState(initialUrl ?? url)

  const isValidUrl = (urlString: string) => {
    try {
      // Add https:// if no protocol is specified
      const urlToTest = urlString.startsWith('http') ? urlString : `https://${urlString}`
      new URL(urlToTest)
      return true
    } catch {
      return false
    }
  }

  const handleSubmit = () => {
    // Add https:// if no protocol is specified
    const normalizedUrl = localUrl.startsWith('http') ? localUrl : `https://${localUrl}`
    setUrl(normalizedUrl)
    onNext(normalizedUrl)
  }

  const canSubmit = localUrl.trim() !== '' && isValidUrl(localUrl)

  return (
    <div className="flex min-h-[100dvh] flex-col items-center justify-center px-6 py-16">
      <div className="animate-fade-up w-full max-w-[560px]">
        {onBack && (
          <button
            onClick={onBack}
            className="text-foreground/70 hover:text-foreground mb-8 flex items-center gap-1.5 text-sm font-medium transition-colors">
            <ArrowLeft size={16} />
            Back
          </button>
        )}
        <h1 className="text-foreground text-4xl font-bold leading-[1.1] tracking-tight lg:text-5xl">
          Do you have a website?
        </h1>
        <p className="text-foreground/70 mt-4 text-lg leading-relaxed">
          We'll read it into your moodboard (colors, logo, and tone) so you start with something,
          not a blank canvas. No website is fine too.
        </p>

        <div className="mt-10 flex items-center gap-2">
          <Input
            value={localUrl}
            onChange={(e) => setLocalUrl(e.target.value)}
            className="h-12 text-base"
            placeholder="your-website.com"
            autoFocus
            onKeyDown={(e) => {
              if (e.key === 'Enter' && canSubmit) {
                handleSubmit()
              }
            }}
          />
          <Button onClick={handleSubmit} disabled={!canSubmit} className="h-12 w-12 shrink-0">
            <ArrowRight />
          </Button>
        </div>
        <button
          onClick={onSkip}
          className="text-muted-foreground hover:text-foreground mt-4 text-sm underline underline-offset-2 transition-colors">
          I don't have a website
        </button>
      </div>
    </div>
  )
}
